/**
 * Drawing Settings Popup Controls UI Helpers.
 *
 * Resolves line style names, Fibonacci extension titles & icons, lock state button attributes,
 * and builds the aggregate view model for drawing popup toolbars.
 */

import { computePositionMetrics } from "./position_tool";

export const DSC_EXTEND_ICON: Record<string, string> = {
  off: "↔",
  custom: "⇥",
};

export const DSC_EXTEND_TITLE: Record<string, string> = {
  off: "Extend: Off (click to enable, then drag the arrow handle to set how far)",
  custom: "Extend: Custom (drag the arrow handle to adjust — click to turn off)",
};

export const DSC_EXTENDABLE_TYPES = ["fib"];
export const DSC_PRICE_EDITABLE_TYPES = ["hline"];
/** Types that get the R:R input in the settings toolbar (sets the target from the risk). */
export const DSC_RR_EDITABLE_TYPES = ["rr_position"];

/**
 * Returns true if the drawing type supports Fibonacci extension toggling.
 */
export function isDrawingExtendable(type: string): boolean {
  return DSC_EXTENDABLE_TYPES.includes(type);
}

/**
 * Returns true if the drawing type supports direct price input editing.
 */
export function isDrawingPriceEditable(type: string): boolean {
  return DSC_PRICE_EDITABLE_TYPES.includes(type);
}

/**
 * Returns true if the drawing type supports the R:R input (Long/Short Position).
 */
export function isDrawingRREditable(type: string): boolean {
  return DSC_RR_EDITABLE_TYPES.includes(type);
}

/**
 * Maps numeric line style enum (0 = solid, 1 = dotted, 2 = dashed) to CSS border style name.
 */
export function getDrawingLineStyleName(
  style: number,
): "solid" | "dotted" | "dashed" {
  if (style === 2) return "dashed";
  if (style === 1) return "dotted";
  return "solid";
}

/**
 * Resolves Fibonacci extend button icon, title, and active class flag.
 */
export function getExtendIconAndTitle(mode: string): {
  icon: string;
  title: string;
  isActive: boolean;
} {
  const m = mode === "custom" ? "custom" : "off";
  return {
    icon: DSC_EXTEND_ICON[m] || DSC_EXTEND_ICON.off,
    title: DSC_EXTEND_TITLE[m] || DSC_EXTEND_TITLE.off,
    isActive: mode !== "off",
  };
}

/**
 * Resolves lock button icon, active class, and delete button disabled flag.
 */
export function getLockStateDisplay(locked: boolean): {
  icon: string;
  isActive: boolean;
  deleteDisabled: boolean;
} {
  const isLocked = !!locked;
  return {
    icon: isLocked ? "🔒" : "🔓",
    isActive: isLocked,
    deleteDisabled: isLocked,
  };
}

export interface DrawingSettingsViewModel {
  color: string;
  opacity: number;
  opacityPercentText: string;
  width: number;
  lineStyle: number;
  styleName: "solid" | "dotted" | "dashed";
  title: string;
  locked: boolean;
  lockIcon: string;
  deleteDisabled: boolean;
  isPriceEditable: boolean;
  priceText: string;
  isExtendable: boolean;
  extendMode: string | null;
  extendIcon: string;
  extendTitle: string;
  isExtendActive: boolean;
  isRREditable: boolean;
  /** Current R:R to 2dp, or "" when not applicable / not a valid position. */
  rrText: string;
}

/**
 * Builds an aggregate view model describing the state of the drawing settings toolbar.
 */
export function buildDrawingSettingsViewModel(
  drawing: any,
  getFibExtendModeFn?: (d: any) => string,
): DrawingSettingsViewModel {
  const color = drawing.color || "#787B86";
  const opacity = drawing.opacity ?? 1;
  const opacityPercentText = `${Math.round(opacity * 100)}%`;
  const width = drawing.lineWidth || 1;
  const lineStyle = drawing.lineStyle !== undefined ? drawing.lineStyle : 2;
  const styleName = getDrawingLineStyleName(lineStyle);
  const title = drawing.title || "";
  const locked = !!drawing.locked;
  const { icon: lockIcon, deleteDisabled } = getLockStateDisplay(locked);

  const priceEditable =
    isDrawingPriceEditable(drawing.type) && drawing.price !== undefined;
  const priceText =
    priceEditable && typeof drawing.price === "number"
      ? drawing.price.toFixed(2)
      : "";

  const extendable = isDrawingExtendable(drawing.type);
  let extendMode: string | null = null;
  let extendIcon = "";
  let extendTitle = "";
  let isExtendActive = false;

  if (extendable && typeof getFibExtendModeFn === "function") {
    extendMode = getFibExtendModeFn(drawing);
    const ext = getExtendIconAndTitle(extendMode);
    extendIcon = ext.icon;
    extendTitle = ext.title;
    isExtendActive = ext.isActive;
  }

  const rrEditable = isDrawingRREditable(drawing.type);
  const metrics = rrEditable ? computePositionMetrics(drawing) : null;
  const rrText = metrics ? metrics.rr.toFixed(2) : "";

  return {
    color,
    opacity,
    opacityPercentText,
    width,
    lineStyle,
    styleName,
    title,
    locked,
    lockIcon,
    deleteDisabled,
    isPriceEditable: priceEditable,
    priceText,
    isExtendable: extendable,
    extendMode,
    extendIcon,
    extendTitle,
    isExtendActive,
    isRREditable: rrEditable,
    rrText,
  };
}
